/**
 * Transfer Fragment
 * Handles transfer functionality:
 * - New transfer form (security mode, optional settings, recipient autocomplete)
 * - File upload for transfers (drag-drop, upload progress, retry)
 */

import { Events } from '../../../js/utils/events.js';

let initialized = false;

const CHROME_UPLOAD_LIMIT_BYTES = 10 * 1024 * 1024 * 1024;
const DEFAULT_UPLOAD_LIMIT_BYTES = 4 * 1024 * 1024 * 1024;

function isChromeBrowser() {
    const ua = navigator.userAgent || '';
    return /Chrome/i.test(ua) && !/Edg/i.test(ua) && !/OPR/i.test(ua);
}

function getMaxUploadSizeBytes() {
    return isChromeBrowser() ? CHROME_UPLOAD_LIMIT_BYTES : DEFAULT_UPLOAD_LIMIT_BYTES;
}

function getUploadLimitLabel() {
    return isChromeBrowser() ? '10 GB' : '4 GB';
}

/**
 * Build a recipient string for the transfer form. Names containing the server's
 * recipient delimiters (`,`, `;`, CR, LF) are wrapped in double quotes so the
 * server parser keeps the full name as a single entry; stray double quotes in
 * the name are stripped before wrapping.
 *
 * @param {string} name
 * @param {string} email
 * @returns {string}
 */
function formatRecipient(name, email) {
    if (!name || name === email) {
        return email;
    }
    if (/[",;\r\n]/.test(name)) {
        const escaped = name.replace(/"/g, '');
        return `"${escaped}" <${email}>`;
    }
    return `${name} <${email}>`;
}

// ============================================================================
// TransferNew - New transfer form handling
// ============================================================================

const TransferNew = {
    // State variables
    selectedFiles: [],
    autocompleteTimeout: null,
    autocompleteResults: [],

    // Toggle optional settings visibility
    onToggleOptionalSettings(e, element) {
        const optionalSettings = document.getElementById('optional-settings');
        const toggleLink = document.getElementById('optional-settings-toggle');

        if (optionalSettings && toggleLink) {
            if (optionalSettings.style.display === 'none') {
                optionalSettings.style.display = '';
                toggleLink.textContent = 'Hide optional settings';
            } else {
                optionalSettings.style.display = 'none';
                toggleLink.textContent = 'Show optional settings';
            }
        }

        // Prevent default link behavior
        if (e) e.preventDefault();
    },

    // Handle security mode radio button changes
    onSecurityModeChange(e, element) {
        const securityQuestion = document.querySelector('input[name="security_mode"][value="S"]');
        const questionFields = document.getElementById('security-question-fields');

        if (securityQuestion && questionFields) {
            if (securityQuestion.checked) {
                questionFields.style.display = '';
            } else {
                questionFields.style.display = 'none';
            }
        }
    },

    // Handle predefined security question radio button changes
    onPredefinedQuestionChange(e, element) {
        const textarea = document.querySelector('textarea[name="security_question"]');
        const customQuestionSection = document.getElementById('custom-question-section');
        const answerInput = document.querySelector('input[name="security_answer"]');

        if (element.value === '__custom__') {
            // Show custom question section
            if (customQuestionSection) {
                customQuestionSection.style.display = '';
            }
            // Clear textarea for custom input and focus it
            if (textarea) {
                textarea.value = '';
                textarea.focus();
            }
        } else {
            // Hide custom question section when predefined is selected
            if (customQuestionSection) {
                customQuestionSection.style.display = 'none';
            }
            // Set the question text from the selected predefined question
            if (textarea) {
                textarea.value = element.value;
            }
            // Focus the answer field so user can type immediately
            if (answerInput) {
                answerInput.focus();
            }
        }
    },

    // Sync predefined question visibility on initial load
    // Called during initialize() to ensure custom question section matches selected radio
    syncPredefinedQuestionVisibility() {
        const checkedRadio = document.querySelector('input[name="predefined_question"]:checked');
        if (!checkedRadio) {
            return; // No predefined questions configured
        }

        const customQuestionSection = document.getElementById('custom-question-section');
        if (!customQuestionSection) {
            return;
        }

        if (checkedRadio.value === '__custom__') {
            // Show custom question section
            customQuestionSection.style.display = '';
        } else {
            // Hide custom question section when predefined is selected
            customQuestionSection.style.display = 'none';
        }
    },

    // Trigger file picker when user clicks attach button
    onAttachFiles(e, element) {
        const fileInput = document.getElementById('file-input');
        if (fileInput) {
            fileInput.click();
        }
    },

    // Handle file selection from file picker
    onFilesSelected(event) {
        const files = event.target.files;
        if (files.length > 0) {
            // Add new files to the selectedFiles array
            for (let i = 0; i < files.length; i++) {
                TransferNew.selectedFiles.push(files[i]);
            }
            TransferNew.updateFileList();
            TransferNew.updateButtonState();
        }
        // Reset file input so same files can be selected again if needed
        event.target.value = '';
    },

    // Remove file from the selected files list
    onRemoveFile(index) {
        TransferNew.selectedFiles.splice(index, 1);
        TransferNew.updateFileList();
        TransferNew.updateButtonState();
    },

    // Format bytes to human-readable file size
    formatFileSize(bytes) {
        if (bytes === 0) return '0 Bytes';

        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    },

    // Render file list UI
    updateFileList() {
        const filesList = document.getElementById('files-list');
        const emptyState = document.getElementById('files-empty-state');

        if (!filesList) return;

        if (TransferNew.selectedFiles.length === 0) {
            // Show empty state
            if (emptyState) {
                emptyState.style.display = 'block';
            }
            // Clear any file items
            const fileItems = filesList.querySelectorAll('.file-item');
            fileItems.forEach(item => item.remove());
        } else {
            // Hide empty state
            if (emptyState) {
                emptyState.style.display = 'none';
            }

            // Clear existing file items
            const existingItems = filesList.querySelectorAll('.file-item');
            existingItems.forEach(item => item.remove());

            // Render each file
            TransferNew.selectedFiles.forEach((file, index) => {
                const fileItem = TransferNew.createFileItemElement(file, index);
                filesList.appendChild(fileItem);
            });
        }
    },

    // Create file item DOM element
    createFileItemElement(file, index) {
        const fileItem = document.createElement('div');
        fileItem.className = 'file-item flex items-center justify-between px-3 py-2 border border-gray-200 rounded-lg bg-gray-50';

        fileItem.innerHTML = `
            <div class="flex items-center gap-2 flex-1 min-w-0">
                <svg class="w-4 h-4 text-gray-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span class="text-sm text-gray-900 font-medium truncate">${TransferNew.escapeHtml(file.name)}</span>
                <span class="text-xs text-gray-500 shrink-0">(${TransferNew.formatFileSize(file.size)})</span>
            </div>
            <button type="button"
                    data-transfer-remove-file="${index}"
                    class="text-gray-400 hover:text-red-600 ml-2 shrink-0"
                    title="Remove file">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        `;

        // Add click handler to remove button
        const removeBtn = fileItem.querySelector('[data-transfer-remove-file]');
        if (removeBtn) {
            removeBtn.addEventListener('click', () => TransferNew.onRemoveFile(index));
        }

        return fileItem;
    },

    // Escape HTML to prevent XSS
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    },

    // Enable/disable Send button based on files
    updateButtonState() {
        const sendButton = document.getElementById('send-transfer-btn');
        if (sendButton) {
            sendButton.disabled = TransferNew.selectedFiles.length === 0;
        }
    },

    // Autocomplete methods
    async onRecipientInput(textarea) {
        // Clear existing timeout
        if (TransferNew.autocompleteTimeout) {
            clearTimeout(TransferNew.autocompleteTimeout);
        }

        // Get current cursor position and text
        const cursorPos = textarea.selectionStart;
        const text = textarea.value.substring(0, cursorPos);

        // Find the start of the current email being typed
        const lastCommaIndex = text.lastIndexOf(',');
        const currentInput = text.substring(lastCommaIndex + 1).trim();

        // Only search if user has typed at least 2 characters
        if (currentInput.length < 2) {
            TransferNew.hideAutocomplete();
            return;
        }

        // Debounce the search
        TransferNew.autocompleteTimeout = setTimeout(async () => {
            await TransferNew.searchContacts(currentInput, textarea);
        }, 300);
    },

    async searchContacts(query, textarea) {
        try {
            const response = await fetch(`/app/contacts/search?q=${encodeURIComponent(query)}`);
            const data = await response.json();

            if (data.status === 200 && data.data && data.data.length > 0) {
                TransferNew.autocompleteResults = data.data;
                TransferNew.showAutocomplete(textarea, data.data);
            } else {
                TransferNew.hideAutocomplete();
            }
        } catch (error) {
            console.error('Error searching contacts:', error);
            TransferNew.hideAutocomplete();
        }
    },

    showAutocomplete(textarea, contacts) {
        // Remove existing dropdown
        TransferNew.hideAutocomplete();

        // Create dropdown element
        const dropdown = document.createElement('div');
        dropdown.id = 'recipient-autocomplete';
        dropdown.className = 'absolute bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto';
        dropdown.style.width = textarea.offsetWidth + 'px';

        // Position dropdown below textarea
        const rect = textarea.getBoundingClientRect();
        dropdown.style.top = (rect.bottom + window.scrollY) + 'px';
        dropdown.style.left = (rect.left + window.scrollX) + 'px';

        // Add contact items
        contacts.forEach((contact, index) => {
            const item = document.createElement('div');
            item.className = 'p-2 hover:bg-surface-muted cursor-pointer border-b border-border-subtle last:border-b-0';
            item.innerHTML = `
                <div class="text-sm font-medium text-gray-900">${TransferNew.escapeHtml(contact.name)}</div>
                <div class="text-xs text-gray-600">${TransferNew.escapeHtml(contact.email)}</div>
            `;
            item.onclick = () => TransferNew.selectContact(textarea, contact);
            dropdown.appendChild(item);
        });

        document.body.appendChild(dropdown);

        // Close dropdown when clicking outside
        setTimeout(() => {
            document.addEventListener('click', TransferNew.handleAutocompleteClickOutside, true);
        }, 0);
    },

    hideAutocomplete() {
        const dropdown = document.getElementById('recipient-autocomplete');
        if (dropdown) {
            dropdown.remove();
        }
        document.removeEventListener('click', TransferNew.handleAutocompleteClickOutside, true);
    },

    handleAutocompleteClickOutside(event) {
        const dropdown = document.getElementById('recipient-autocomplete');
        const textarea = document.querySelector('textarea[name="recipients"]');

        if (dropdown && !dropdown.contains(event.target) && event.target !== textarea) {
            TransferNew.hideAutocomplete();
        }
    },

    selectContact(textarea, contact) {
        const cursorPos = textarea.selectionStart;
        const text = textarea.value;

        // Find the start of the current email being typed
        const beforeCursor = text.substring(0, cursorPos);
        const lastCommaIndex = beforeCursor.lastIndexOf(',');
        const startPos = lastCommaIndex >= 0 ? lastCommaIndex + 1 : 0;

        // Find the end of the current email (next comma or end of string)
        const afterCursor = text.substring(cursorPos);
        const nextCommaIndex = afterCursor.indexOf(',');
        const endPos = nextCommaIndex >= 0 ? cursorPos + nextCommaIndex : text.length;

        // Replace the current input with selected contact
        const contactStr = formatRecipient(contact.name, contact.email);
        const before = text.substring(0, startPos).trim();
        const after = text.substring(endPos).trim();

        let newValue;
        if (before && after) {
            newValue = before + ', ' + contactStr + ', ' + after;
        } else if (before) {
            newValue = before + ', ' + contactStr;
        } else if (after) {
            newValue = contactStr + ', ' + after;
        } else {
            newValue = contactStr;
        }

        textarea.value = newValue;

        // Position cursor after the inserted contact
        const newCursorPos = (before ? before.length + 2 : 0) + contactStr.length;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
        textarea.focus();

        TransferNew.hideAutocomplete();
    },

    setupRecipientAutocomplete() {
        const textarea = document.querySelector('textarea[name="recipients"]');
        if (textarea) {
            textarea.addEventListener('input', () => TransferNew.onRecipientInput(textarea));
            textarea.addEventListener('keydown', (e) => {
                // Close dropdown on Escape
                if (e.key === 'Escape') {
                    TransferNew.hideAutocomplete();
                }
            });
        }
    },

    // Clean up transfer resources
    cleanup() {
        // Reset selected files
        TransferNew.selectedFiles = [];

        // Reset file input
        const fileInput = document.getElementById('file-input');
        if (fileInput) {
            fileInput.value = '';
        }

        // Clear autocomplete
        TransferNew.hideAutocomplete();
        if (TransferNew.autocompleteTimeout) {
            clearTimeout(TransferNew.autocompleteTimeout);
        }
    },

    // Initialize transfer form functionality
    initialize() {
        // Reset state
        TransferNew.selectedFiles = [];

        // Update UI
        TransferNew.updateFileList();
        TransferNew.updateButtonState();

        // Setup autocomplete
        TransferNew.setupRecipientAutocomplete();

        // Initialize security mode display
        TransferNew.onSecurityModeChange();

        // Sync predefined question visibility (for re-rendered forms)
        TransferNew.syncPredefinedQuestionVisibility();
    }
};

// Make available globally for backward compatibility during transition
window.TransferNew = TransferNew;

// ============================================================================
// TransferFileUpload - Drag-drop file upload component
// ============================================================================

const TransferFileUpload = {
    files: [],
    uploadedFiles: [],
    transferOid: null,
    // Upload retry state
    lastError: null,
    uploadState: 'idle', // idle | uploading | failed | completed
    // Track original totals for progress calculation during retry
    originalTotalBytes: 0,
    // Track bytes of successfully completed files (for accurate progress on retry)
    completedBytes: 0,
    // XHR references
    currentXhr: null,
    uploadCancelled: false,
    uploadAction: null,
    // When true, a successful 'addAndSave' upload opens the schedule modal
    // instead of navigating to the draft detail page (set by "Add and Schedule").
    pendingScheduleModal: false,

    init() {
        const uploadElement = document.getElementById('file-upload');
        if (!uploadElement) {
            console.log('TransferFileUpload: file-upload element not found');
            return;
        }

        // Clear files array when initializing (prevents files from previous transfers persisting)
        this.files = [];
        this.uploadedFiles = [];
        this.lastError = null;
        this.pendingScheduleModal = false;
        this.uploadState = 'idle';
        this.originalTotalBytes = 0;
        this.completedBytes = 0;

        this.transferOid = uploadElement.dataset.transferOid;
        console.log('TransferFileUpload: Initialized with transfer OID:', this.transferOid);
        this.setupDropZone();
        this.displayFiles();
    },

    // Alias for compatibility
    initialize() {
        this.init();
    },

    setupDropZone() {
        const dropZone = document.getElementById('drop-zone');
        if (!dropZone) {
            console.log('TransferFileUpload: drop-zone element not found');
            return;
        }
        console.log('TransferFileUpload: Setting up drop zone');

        // Prevent default drag behaviors
        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
            dropZone.addEventListener(eventName, this.preventDefaults, false);
            document.body.addEventListener(eventName, this.preventDefaults, false);
        });

        // Highlight drop zone when item is dragged over it
        ['dragenter', 'dragover'].forEach(eventName => {
            dropZone.addEventListener(eventName, () => {
                dropZone.classList.add('border-teal-600', 'bg-status-info-bg');
            }, false);
        });

        ['dragleave', 'drop'].forEach(eventName => {
            dropZone.addEventListener(eventName, () => {
                dropZone.classList.remove('border-teal-600', 'bg-status-info-bg');
            }, false);
        });

        // Handle dropped files
        const self = this;
        dropZone.addEventListener('drop', async (e) => {
            // MUST prevent default synchronously before async operations
            e.preventDefault();
            e.stopPropagation();

            const dt = e.dataTransfer;

            // Use DataTransferItemList API to handle folders properly
            if (dt.items) {
                const files = await self.getAllFileEntries(dt.items);
                self.handleFiles(files);
            } else {
                // Fallback for older browsers
                self.handleFiles(dt.files);
            }
        }, false);
    },

    // Recursively get all file entries from dropped items (handles folders)
    async getAllFileEntries(dataTransferItemList) {
        const files = [];

        // Convert dataTransferItemList to array and get entries SYNCHRONOUSLY
        // This is critical because DataTransferItem objects are cleared after
        // the event handler's synchronous execution completes
        const entries = [];
        for (let i = 0; i < dataTransferItemList.length; i++) {
            const item = dataTransferItemList[i];
            if (item.kind === 'file') {
                const entry = item.webkitGetAsEntry();
                if (entry) {
                    entries.push(entry);
                }
            }
        }

        // Now process entries asynchronously (safe since we captured them above)
        for (const entry of entries) {
            await this.traverseFileTree(entry, '', files);
        }

        return files;
    },

    // Recursively traverse file tree and collect files
    async traverseFileTree(item, path, files) {
        if (item.isFile) {
            // Get the file
            const file = await new Promise((resolve, reject) => {
                item.file(resolve, reject);
            });

            // Add relative path to file object (mimics webkitRelativePath)
            const relativePath = path + file.name;
            Object.defineProperty(file, 'webkitRelativePath', {
                value: relativePath,
                writable: false
            });

            files.push(file);
        } else if (item.isDirectory) {
            // Get directory reader
            const dirReader = item.createReader();

            // Read all entries in directory
            const entries = await new Promise((resolve, reject) => {
                dirReader.readEntries(resolve, reject);
            });

            // Recursively process each entry
            for (const entry of entries) {
                await this.traverseFileTree(entry, path + item.name + '/', files);
            }
        }
    },

    preventDefaults(e) {
        e.preventDefault();
        e.stopPropagation();
    },

    onSelectFiles() {
        document.getElementById('file-input')?.click();
    },

    onSelectFolder() {
        document.getElementById('folder-input')?.click();
    },

    onFilesSelected(event) {
        const files = event.target.files;
        this.handleFiles(files);
    },

    handleFiles(files) {
        console.log('TransferFileUpload: Handling files:', files.length);
        // Convert FileList to Array
        const filesArray = Array.from(files);

        // Filter out duplicates (matching Java: same path and size)
        const uniqueNewFiles = filesArray.filter(newFile => {
            const newPath = newFile.webkitRelativePath || newFile.name;
            const newSize = newFile.size;

            // Check if this file already exists in our files array
            const isDuplicate = this.files.some(existingFile => {
                const existingPath = existingFile.webkitRelativePath || existingFile.name;
                const existingSize = existingFile.size;
                return existingPath === newPath && existingSize === newSize;
            });

            return !isDuplicate; // Only keep non-duplicates
        });

        // Add only unique files to our files array
        const potentialFiles = [...this.files, ...uniqueNewFiles];

        // Check total size before adding files (limit varies by browser)
        const totalSize = potentialFiles.reduce((sum, file) => sum + file.size, 0);
        const maxSize = getMaxUploadSizeBytes();
        const tooLargeFile = potentialFiles.find(file => file.size > maxSize);

        if (tooLargeFile) {
            alert(`File size exceeds ${getUploadLimitLabel()}!\n\nWe are unable to support uploads that are larger than ${getUploadLimitLabel()} at this time.`);
            return;
        }

        if (totalSize > maxSize) {
            alert(`Size of the upload exceeds ${getUploadLimitLabel()}!\n\nWe are unable to support uploads that are larger than ${getUploadLimitLabel()} at this time.`);
            return;
        }

        this.files = potentialFiles;
        console.log('TransferFileUpload: Total files now:', this.files.length);
        this.displayFiles();
    },

    displayFiles() {
        const filesList = document.getElementById('files-list');
        if (!filesList) return;

        // Clear current display
        filesList.innerHTML = '';

        // Display each file in simple format
        this.files.forEach((file, index) => {
            const fileItem = document.createElement('div');
            fileItem.className = 'flex items-center justify-between text-sm text-gray-700 hover:bg-gray-50 py-1 px-2 rounded-sm';

            // Use webkitRelativePath if available (folder selection), otherwise use file.name
            // Add leading slash for folder paths
            let displayPath = file.webkitRelativePath || file.name;
            if (file.webkitRelativePath) {
                displayPath = '/' + displayPath;
            }

            fileItem.innerHTML = `
                <span class="truncate flex-1 min-w-0">${this.escapeHtml(displayPath)}</span>
                <span class="ml-4 text-gray-500 shrink-0 whitespace-nowrap">${this.formatFileSize(file.size)}</span>
                <button type="button"
                        class="ml-3 text-gray-400 hover:text-red-600 shrink-0 transition-colors"
                        data-file-index="${index}"
                        title="Remove file">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                </button>
            `;

            // Add click handler to the remove button
            const removeBtn = fileItem.querySelector('button[data-file-index]');
            if (removeBtn) {
                removeBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.removeFile(index);
                });
            }

            filesList.appendChild(fileItem);
        });
    },

    formatFileSize(bytes) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },

    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    },

    removeFile(index) {
        this.files.splice(index, 1);
        this.displayFiles();
    },

    async uploadFiles(action, isRetry = false) {
        // Check if there are files to upload
        const totalSize = this.files.reduce((sum, file) => sum + file.size, 0);

        if (this.files.length === 0) {
            alert('Nothing to send!');
            this.pendingScheduleModal = false;
            return;
        }

        if (!this.transferOid) {
            alert('Transfer OID is missing');
            this.pendingScheduleModal = false;
            return;
        }

        // Check total file size before upload (limit varies by browser)
        const maxSize = getMaxUploadSizeBytes();
        const tooLargeFile = this.files.find(file => file.size > maxSize);

        if (tooLargeFile) {
            alert(`File size exceeds ${getUploadLimitLabel()}!\n\nWe are unable to support uploads that are larger than ${getUploadLimitLabel()} at this time.`);
            this.pendingScheduleModal = false;
            return;
        }

        if (totalSize > maxSize) {
            alert(`Size of the upload exceeds ${getUploadLimitLabel()}!\n\nWe are unable to support uploads that are larger than ${getUploadLimitLabel()} at this time.`);
            this.pendingScheduleModal = false;
            return;
        }

        // Initialize upload state
        this.uploadCancelled = false;
        this.uploadAction = action;
        this.currentXhr = null;

        // Disable buttons during upload
        const sendBtn = document.getElementById('send-transfer-btn');
        const saveBtn = document.getElementById('add-save-btn');
        const scheduleBtn = document.getElementById('add-schedule-btn');
        if (sendBtn) sendBtn.disabled = true;
        if (saveBtn) saveBtn.disabled = true;
        if (scheduleBtn) scheduleBtn.disabled = true;

        // Check sizes and show the modal on the first upload, not on retry.
        if (!isRetry) {
            // Store original total for progress calculation during retry
            this.originalTotalBytes = this.files.reduce((sum, file) => sum + file.size, 0);
            this.completedBytes = 0;

            // Refuse oversized files before a single byte goes out.
            const refusal = await this.initializeProgress();

            // The account limit refused a file — say so now, before uploading it.
            if (refusal) {
                alert(refusal);
                this.pendingScheduleModal = false;
                if (sendBtn) sendBtn.disabled = false;
                if (saveBtn) saveBtn.disabled = false;
                if (scheduleBtn) scheduleBtn.disabled = false;
                return;
            }

            // Show progress modal
            this.showProgressModal();
        }

        try {
            let lastResult = null;
            // Use while loop and remove uploaded files from array to prevent re-upload on retry
            while (this.files.length > 0) {
                // Check if upload was cancelled
                if (this.uploadCancelled) {
                    console.log('[UPLOAD] Upload cancelled by user');
                    break;
                }

                // Always process the first file in the array
                const file = this.files[0];
                const isLastFile = (this.files.length === 1);

                lastResult = await this.uploadFile(file, isLastFile, action);

                // Track completed bytes for accurate retry progress
                this.completedBytes += file.size;

                // Remove successfully uploaded file from array
                this.files.shift();
                // Update UI to reflect remaining files
                this.displayFiles();
            }

            // Close modal
            this.hideProgressModal();

            // Check if upload was cancelled
            if (this.uploadCancelled) {
                console.log('[UPLOAD] Upload cancelled by user');
                return;
            }

            // Determine redirect URL based on action and transfer_oid
            let redirectUrl;

            if (lastResult && lastResult.data) {
                console.log('[UPLOAD] Last result:', lastResult);
                console.log('[UPLOAD] Data:', lastResult.data);

                if (action === 'addAndSend') {
                    // For sent transfers, redirect to confirmation page
                    const transferOid = lastResult.data.transfer_oid || this.transferOid;
                    console.log('[UPLOAD] Transfer OID for confirmation redirect:', transferOid);

                    if (transferOid) {
                        redirectUrl = `/app/transfers/confirmation/${transferOid}`;
                    } else {
                        // Fallback to outgoing list if no OID available
                        console.warn('[UPLOAD] No transfer OID available, redirecting to outgoing list');
                        redirectUrl = '/app/transfers/outgoing';
                    }
                } else {
                    // For drafts, redirect to the specific draft detail page
                    const transferOid = lastResult.data.transfer_oid || this.transferOid;
                    console.log('[UPLOAD] Transfer OID for redirect:', transferOid);

                    if (this.pendingScheduleModal) {
                        // "Add and Schedule": the draft is saved — open the schedule
                        // modal in place instead of navigating to the draft detail page.
                        this.pendingScheduleModal = false;
                        if (transferOid) {
                            console.log('[UPLOAD] Draft saved, opening schedule modal for:', transferOid);
                            htmx.ajax('GET', `/app/transfers/drafts/transfer/${transferOid}/schedule-modal?on_close=draft`, {
                                target: '#schedule-modal-host',
                                swap: 'innerHTML'
                            });
                        } else {
                            console.warn('[UPLOAD] No transfer OID available, redirecting to drafts list');
                            window.location.href = '/app/transfers/drafts';
                        }
                        return;
                    }

                    if (transferOid) {
                        redirectUrl = `/app/transfers/drafts/transfer/${transferOid}`;
                    } else {
                        // Fallback to drafts list if no OID available
                        console.warn('[UPLOAD] No transfer OID available, redirecting to drafts list');
                        redirectUrl = '/app/transfers/drafts';
                    }
                }

                console.log('[UPLOAD] Redirecting to:', redirectUrl);

                // Redirect using window.location for full page navigation
                window.location.href = redirectUrl;
            } else {
                // Fallback to old behavior if no result data
                if (action === 'addAndSend') {
                    const transferOid = this.transferOid;
                    if (transferOid) {
                        window.location.href = `/app/transfers/confirmation/${transferOid}`;
                    } else {
                        window.location.href = '/app/transfers/outgoing';
                    }
                } else if (this.pendingScheduleModal && this.transferOid) {
                    this.pendingScheduleModal = false;
                    htmx.ajax('GET', `/app/transfers/drafts/transfer/${this.transferOid}/schedule-modal?on_close=draft`, {
                        target: '#schedule-modal-host',
                        swap: 'innerHTML'
                    });
                } else {
                    this.pendingScheduleModal = false;
                    window.location.href = '/app/transfers/drafts';
                }
            }
        } catch (error) {
            if (!this.uploadCancelled) {
                // Store error state for retry
                this.uploadState = 'failed';
                this.lastError = error.message;
                console.error('[UPLOAD] Upload failed:', error.message);
                // Show retry UI with specific error message
                this.showRetryUI(error.message);
            } else {
                this.hideProgressModal();
            }
        } finally {
            // Re-enable buttons
            if (sendBtn) sendBtn.disabled = false;
            if (saveBtn) saveBtn.disabled = false;
            if (scheduleBtn) scheduleBtn.disabled = false;
        }
    },

    // Returns the server's refusal message, or null when the upload may start.
    async initializeProgress() {
        const response = await fetch(`/app/transfers/upload/init-progress/${this.transferOid}`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                // The server checks these against the account's file size limit
                // before we send a single byte of any of them.
                files: this.files.map(file => ({
                    name: file.webkitRelativePath || file.name,
                    size: file.size
                }))
            })
        });
        if (!response.ok) {
            console.error('Failed to check file sizes before upload');
            return null;
        }
        // An expired session answers with the login page, not JSON. Let the upload
        // proceed and fail in the handled path rather than throwing out of here.
        let result;
        try {
            result = await response.json();
        } catch {
            console.error('Failed to check file sizes before upload');
            return null;
        }
        if (result.success === false) {
            console.warn('[UPLOAD] Refused before start:', result.error);
            return result.error || 'Upload failed';
        }
        return null;
    },

    setProgressBarUI(percent) {
        // Directly update the progress bar UI (used for retry to show correct starting point)
        const progressBar = document.getElementById('modal-progress-bar');
        const progressText = document.getElementById('modal-progress-text');
        if (progressBar) {
            progressBar.style.width = percent + '%';
        }
        if (progressText) {
            progressText.textContent = percent + '%';
        }
    },

    showProgressModal() {
        // Show progress modal
        const modal = document.getElementById('upload-progress-modal');
        if (modal) {
            modal.classList.remove('hidden');
        }
    },

    hideProgressModal() {
        // Hide progress modal
        const modal = document.getElementById('upload-progress-modal');
        if (modal) {
            modal.classList.add('hidden');
        }
    },

    showRetryUI(errorMessage) {
        // Change progress bar to red to indicate error
        const progressBar = document.getElementById('modal-progress-bar');
        if (progressBar) {
            progressBar.classList.remove('bg-teal-600');
            progressBar.classList.add('bg-status-danger-text');
        }

        // Show error label with specific message
        const errorLabel = document.getElementById('upload-error-label');
        if (errorLabel) {
            if (errorMessage) errorLabel.textContent = errorMessage;
            errorLabel.classList.remove('hidden');
        }

        // Hide cancel button, show retry buttons
        const cancelSection = document.getElementById('cancel-button-section');
        const retrySection = document.getElementById('retry-button-section');
        if (cancelSection) {
            cancelSection.classList.add('hidden');
        }
        if (retrySection) {
            retrySection.classList.remove('hidden');
        }
    },

    resetProgressModalUI() {
        // Reset progress bar color to blue
        const progressBar = document.getElementById('modal-progress-bar');
        if (progressBar) {
            progressBar.classList.remove('bg-status-danger-text');
            progressBar.classList.add('bg-teal-600');
        }

        // Hide error label
        const errorLabel = document.getElementById('upload-error-label');
        if (errorLabel) {
            errorLabel.classList.add('hidden');
        }

        // Show cancel button, hide retry buttons
        const cancelSection = document.getElementById('cancel-button-section');
        const retrySection = document.getElementById('retry-button-section');
        if (cancelSection) {
            cancelSection.classList.remove('hidden');
        }
        if (retrySection) {
            retrySection.classList.add('hidden');
        }
    },

    async retryUpload() {
        console.log('[UPLOAD] Retrying upload...');
        this.uploadState = 'uploading';
        this.lastError = null;

        // Reset the modal UI back to progress view
        this.resetProgressModalUI();

        // Calculate and set the correct progress percentage based on completed bytes
        const completedPercent = this.originalTotalBytes > 0
            ? Math.floor((this.completedBytes / this.originalTotalBytes) * 100)
            : 0;
        this.setProgressBarUI(completedPercent);
        console.log('[RETRY] Starting from', completedPercent + '%', 'completedBytes:', this.completedBytes, 'totalBytes:', this.originalTotalBytes);

        // Resume upload - this.files still contains the failed file + remaining files
        // Pass isRetry=true to skip the size check, already passed
        await this.uploadFiles(this.uploadAction, true);
    },

    async cancelRetry() {
        console.log('[UPLOAD] Cancel retry requested');
        this.uploadState = 'idle';
        this.lastError = null;
        this.pendingScheduleModal = false;
        // Reset the modal UI and hide it
        this.resetProgressModalUI();
        this.hideProgressModal();
    },

    onCancelUpload() {
        console.log('[UPLOAD] Cancel requested');

        // Set cancel flag
        this.uploadCancelled = true;
        this.pendingScheduleModal = false;

        // Abort current file upload if in progress
        if (this.currentXhr) {
            this.currentXhr.abort();
        }

        // Hide modal
        this.hideProgressModal();
    },

    async uploadFile(file, isLastFile, action) {
        // Use webkitRelativePath if available (folder upload), otherwise use file.name
        const fileName = file.webkitRelativePath || file.name;
        console.log('[UPLOAD] Starting upload:', fileName, file.size, 'bytes');

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            this.currentXhr = xhr;

            xhr.open('POST', '/app/transfers/upload/stream', true);

            // Set headers with file metadata
            xhr.setRequestHeader('X-Transfer-OID', this.transferOid);
            xhr.setRequestHeader('Transfer-File-Name', encodeURIComponent(fileName));
            xhr.setRequestHeader('Transfer-File-Size', file.size.toString());
            xhr.setRequestHeader('Transfer-Last-File', isLastFile ? 'true' : 'false');
            xhr.setRequestHeader('Transfer-Action', action);
            xhr.setRequestHeader('Content-Type', 'application/octet-stream');

            // A hang shows up as absence of movement: xhr.timeout bounds the whole
            // request, so any value short enough to catch one also kills slow multi-GB uploads.
            let stalled = false;
            let watchdog = null;
            const armWatchdog = (ms) => {
                clearTimeout(watchdog);
                watchdog = setTimeout(() => { stalled = true; xhr.abort(); }, ms);
            };

            // Measured in the browser: a server-side counter needs state shared
            // across workers, and split across two it froze the bar at 0%.
            xhr.upload.onprogress = (ev) => {
                armWatchdog(90000);
                if (!ev.lengthComputable || !this.originalTotalBytes) return;
                this.setProgressBarUI(Math.min(100, Math.floor(
                    ((this.completedBytes + ev.loaded) / this.originalTotalBytes) * 100)));
            };

            // Body fully sent: no more progress events, so bound the wait for the
            // response instead. ev.loaded counts bytes handed to the socket, not
            // bytes written to S3, so say what the full bar is still waiting on.
            xhr.upload.onload = () => {
                armWatchdog(180000);
                // Only the last file: mid-queue the bar still has files to climb, so the label reads as a false finish.
                if (!isLastFile) return;
                const header = document.getElementById('modal-header-text');
                if (header) header.textContent = 'Finishing upload...';
            };

            xhr.onload = () => {
                clearTimeout(watchdog);
                this.currentXhr = null;
                if (xhr.status >= 200 && xhr.status < 300) {
                    try {
                        const result = JSON.parse(xhr.responseText);
                        if (result.success) {
                            // Log if file was skipped (already exists)
                            if (result.data?.skipped) {
                                console.log('[UPLOAD] File already exists, skipped:', fileName);
                            } else {
                                console.log('[UPLOAD] File uploaded successfully:', fileName);
                            }
                            this.uploadedFiles.push(fileName);
                            resolve(result);
                        } else {
                            // Handle application-level errors (returned as HTTP 200 to avoid proxy interception)
                            const errorStatus = result.error_status || 400;

                            // For storage quota errors, use HTMX to show error page
                            if (errorStatus === 403 && result.error && result.error.includes('Storage quota exceeded')) {
                                const params = new URLSearchParams({ message: result.error });
                                htmx.ajax('GET', '/app/account/utilization/capacity-warning?' + params.toString(), {
                                    target: '[data-hx-boundary="section"]',
                                    swap: 'innerHTML'
                                });
                                history.pushState({}, '', '/app/account/utilization/capacity-warning');
                                return;
                            }

                            // For transfer limit errors, show draft page with error via POST
                            if (errorStatus === 403 && result.error && result.error.includes('You\'ve hit your transfer limit')) {
                                // POST to get draft page with error message rendered
                                htmx.ajax('POST', '/app/transfers/drafts/transfer/' + this.transferOid + '/with-error', {
                                    target: '[data-hx-boundary="section"]',
                                    swap: 'innerHTML',
                                    values: { error_message: result.error }
                                });
                                history.pushState({}, '', '/app/transfers/drafts/transfer/' + this.transferOid);
                                return;
                            }

                            reject(new Error(result.error || 'Upload failed'));
                        }
                    } catch (e) {
                        reject(new Error('Invalid response from server'));
                    }
                } else {
                    let errorMsg = `Upload failed with status ${xhr.status}`;
                    try {
                        const result = JSON.parse(xhr.responseText);
                        if (result.error) errorMsg = result.error;
                    } catch (e) {}
                    reject(new Error(errorMsg));
                }
            };

            xhr.onerror = () => {
                clearTimeout(watchdog);
                this.currentXhr = null;
                console.log('[UPLOAD] XHR error for file:', fileName);
                reject(new Error('Network error during upload'));
            };

            xhr.onabort = () => {
                clearTimeout(watchdog);
                this.currentXhr = null;
                console.log('[UPLOAD] Upload aborted for file:', fileName);
                reject(new Error(stalled ? 'The server stopped responding.' : 'Upload cancelled'));
            };

            // Send raw file body (not multipart) for true streaming
            const header = document.getElementById('modal-header-text');
            if (header) header.textContent = 'Uploading Files';
            armWatchdog(90000);
            xhr.send(file);
        });
    },

    abortCurrentUpload() {
        if (this.currentXhr) {
            this.currentXhr.abort();
            this.currentXhr = null;
        }
    },

    onSendTransfer() {
        // Explicit intent per click: a plain send must never open the schedule
        // modal, even if a prior "Add and Schedule" attempt failed mid-upload.
        this.pendingScheduleModal = false;
        this.uploadFiles('addAndSend');
    },

    onAddAndSave() {
        // Explicit intent per click: a plain save must never open the schedule
        // modal, even if a prior "Add and Schedule" attempt failed mid-upload.
        this.pendingScheduleModal = false;
        this.uploadFiles('addAndSave');
    },

    onAddAndSchedule() {
        // Save the draft first (same as "Add and Save"), then open the
        // schedule modal instead of navigating to the draft detail page.
        this.pendingScheduleModal = true;
        this.uploadFiles('addAndSave');
    }
};

// Make available globally for backward compatibility during transition
window.TransferFileUpload = TransferFileUpload;

// ============================================================================
// Initialization
// ============================================================================


// ============================================================================
// Message Length (the new-transfer form and the draft edit form)
// Both carried their own copy of this counter. Everything is scoped to the
// form, so the draft form — which has no recipients field and no upload
// button — gets the same rules with the parts it does not have left out.
// Template contract:
//   <form data-compose-form data-message-max="2000">
//     <input data-compose-recipients>
//     <textarea data-compose-message>
//     <p data-compose-counter></p>
//     <button data-compose-requires-recipients>
//     <button data-compose-save>
// ============================================================================

function updateComposeState(form) {
    const message = form.querySelector('[data-compose-message]');
    const counter = form.querySelector('[data-compose-counter]');
    const recipients = form.querySelector('[data-compose-recipients]');
    const max = Number(form.dataset.messageMax);
    if (!message) return;

    const overLimit = message.value.length > max;
    if (counter) {
        counter.textContent = (max - message.value.length) + ' characters remaining';
        counter.classList.toggle('text-red-600', overLimit);
        counter.classList.toggle('text-text-tertiary', !overLimit);
    }

    const save = form.querySelector('[data-compose-save]');
    if (save) save.disabled = overLimit;

    // Upload also needs somewhere to send to.
    const needsRecipients = form.querySelector('[data-compose-requires-recipients]');
    if (needsRecipients) {
        needsRecipients.disabled = overLimit || !recipients || !recipients.value.trim();
    }
}

function initComposeState() {
    Events.on('input', 'compose-message', (event, element) => {
        const form = element.closest('[data-compose-form]');
        if (form) updateComposeState(form);
    });
    Events.on('input', 'compose-recipients', (event, element) => {
        const form = element.closest('[data-compose-form]');
        if (form) updateComposeState(form);
    });
}

function activateComposeState(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('[data-compose-form]').forEach(updateComposeState);
}

export function initTransferCompose() {
    if (initialized) return;
    initialized = true;

    initComposeState();
    activateComposeState(document);
    // The draft edit form comes back through htmx swaps.
    document.addEventListener('htmx:afterSettle', (event) => {
        activateComposeState(event.target);
    });

    // --- TransferNew events ---

    // Security mode radio buttons
    Events.on('change', 'transfer-security-mode', (e, element) => {
        TransferNew.onSecurityModeChange(e, element);
    });

    // Predefined security question radio buttons
    Events.on('change', 'transfer-predefined-question', (e, element) => {
        TransferNew.onPredefinedQuestionChange(e, element);
    });

    // Optional settings toggle
    Events.on('click', 'transfer-optional-toggle', (e, element) => {
        TransferNew.onToggleOptionalSettings(e, element);
    });

    // File input change for new transfer form
    Events.on('change', 'transfer-file-input', (e, element) => {
        TransferNew.onFilesSelected(e);
    });

    // Attach files button
    Events.on('click', 'transfer-attach-files', (e, element) => {
        TransferNew.onAttachFiles(e, element);
    });

    // Send transfer select all checkbox (repository/portal send form)
    Events.on('change', 'transfer-send-select-all', (e, element) => {
        const checked = element.checked;
        const checkboxes = document.querySelectorAll('.send-file-checkbox');
        checkboxes.forEach(checkbox => {
            checkbox.checked = checked;
        });
    });

    // --- TransferFileUpload events ---

    // File input change handlers
    Events.on('change', 'transfer-upload-file-input', (e, element) => {
        TransferFileUpload.onFilesSelected(e);
    });

    Events.on('change', 'transfer-upload-folder-input', (e, element) => {
        TransferFileUpload.onFilesSelected(e);
    });

    // Select files/folder buttons
    Events.on('click', 'transfer-upload-select-files', () => {
        TransferFileUpload.onSelectFiles();
    });

    Events.on('click', 'transfer-upload-select-folder', () => {
        TransferFileUpload.onSelectFolder();
    });

    // Send/Save buttons
    Events.on('click', 'transfer-upload-send', () => {
        TransferFileUpload.onSendTransfer();
    });

    Events.on('click', 'transfer-upload-save', () => {
        TransferFileUpload.onAddAndSave();
    });

    Events.on('click', 'transfer-upload-schedule', () => {
        TransferFileUpload.onAddAndSchedule();
    });

    // Cancel/Retry buttons
    Events.on('click', 'transfer-upload-cancel', () => {
        TransferFileUpload.onCancelUpload();
    });

    Events.on('click', 'transfer-upload-retry', () => {
        TransferFileUpload.retryUpload();
    });

    Events.on('click', 'transfer-upload-cancel-retry', () => {
        TransferFileUpload.cancelRetry();
    });

    // --- Initialize components ---

    // Initialize TransferNew if any transfer form exists (new, forward, or send)
    if (document.getElementById('new-transfer') ||
        document.getElementById('forward-transfer') ||
        document.getElementById('send-transfer')) {
        TransferNew.initialize();
    }

    // Initialize TransferFileUpload if file-upload element exists
    if (document.getElementById('file-upload')) {
        console.log('Initializing TransferFileUpload on DOMContentLoaded');
        TransferFileUpload.initialize();
    }

    // Re-initialize when fragments are dynamically loaded via HTMX
    document.addEventListener('htmx:afterSwap', (event) => {
        // Check if any transfer form exists within the swapped content or is the swap target itself
        const transferFormIds = ['new-transfer', 'forward-transfer', 'send-transfer'];
        const hasTransferForm = transferFormIds.includes(event.target.id) ||
                                transferFormIds.some(id => event.target.querySelector?.(`#${id}`));
        if (hasTransferForm) {
            TransferNew.cleanup();
            TransferNew.initialize();
        }
        if (document.getElementById('file-upload')) {
            console.log('Initializing TransferFileUpload on htmx:afterSwap');
            TransferFileUpload.initialize();
        }
    });

    // Clean up on page unload
    window.addEventListener('beforeunload', () => TransferNew.cleanup());

    console.log('[LeapFILE] Transfer fragment initialized');
}
